import React from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
  useTheme,
} from '@mui/material';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'size'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'danger';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled,
  icon,
  startIcon,
  endIcon,
  sx,
  ...props
}) => {
  const theme = useTheme();

  // Map custom variants to MUI variants and colors
  const getMuiProps = () => {
    switch (variant) {
      case 'primary':
        return { variant: 'contained' as const, color: 'primary' as const };
      case 'secondary':
        return { variant: 'contained' as const, color: 'secondary' as const };
      case 'outline':
        return { variant: 'outlined' as const, color: 'primary' as const };
      case 'text':
        return { variant: 'text' as const, color: 'primary' as const };
      case 'danger':
        return { variant: 'contained' as const, color: 'error' as const };
      default:
        return { variant: 'contained' as const, color: 'primary' as const };
    }
  };

  const muiProps = getMuiProps();

  // Handle loading state icons
  const getStartIcon = () => {
    if (loading) {
      return <CircularProgress size={16} color="inherit" />;
    }
    if (icon && !endIcon) {
      return icon;
    }
    return startIcon;
  };

  const getEndIcon = () => {
    if (loading) {
      return null;
    }
    if (icon && endIcon) {
      return icon;
    }
    return endIcon;
  };

  // Custom styling based on variant
  const getCustomStyles = () => {
    const baseStyles = {
      fontWeight: 600,
      textTransform: 'none' as const,
      borderRadius: 2,
      minHeight: {
        small: 32,
        medium: 40,
        large: 48,
      }[size],
      fontSize: {
        small: '0.75rem',
        medium: '0.875rem',
        large: '1rem',
      }[size],
      px: {
        small: 1.5,
        medium: 2,
        large: 3,
      }[size],
    };

    switch (variant) {
      case 'outline':
        return {
          ...baseStyles,
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.main + '08',
            borderColor: theme.palette.primary.dark,
          },
        };
      case 'text':
        return {
          ...baseStyles,
          color: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.primary.main + '08',
          },
        };
      case 'danger':
        return {
          ...baseStyles,
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
          },
        };
      default:
        return baseStyles;
    }
  };

  return (
    <MuiButton
      {...muiProps}
      {...props}
      size={size}
      disabled={disabled || loading}
      startIcon={getStartIcon()}
      endIcon={getEndIcon()}
      sx={{
        ...getCustomStyles(),
        ...sx,
      }}
    >
      {children}
    </MuiButton>
  );
};

export default Button;